import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SxProps, TextField, Theme } from '@mui/material';
import { type CountryCode, isValidPhoneNumber, parseDigits } from 'libphonenumber-js';
import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';

import { countryCodeLib, formatPhoneNumber, getCountry, getCountryCallingCode } from './utils';

interface PhoneNumberInputProps {
	size?: 'small' | 'medium';
	phoneNumber: string;
	countryCode: number;
	readOnly: boolean;
	required?: boolean;
	label?: string;
	placeholder?: string;
	codeInputName?: string;
	phoneInputName?: string;
	styles?: CSSProperties;
	codeStyles?: SxProps<Theme>;
	codeSx?: SxProps<Theme>;
	sx?: SxProps<Theme>;
	onPhoneChange: (value: string) => void;
	onCountryCodeChange: (value: number) => void;
	onValidityChange?: (isValid: boolean) => void;
	hideCodeInputLabel?: boolean;
	setExternalIsPhoneValid?: (isValid: boolean) => void;
	disabled?: boolean;
	externalIsPhoneValid?: boolean;
}

export const PhoneNumberInput = ({
	size = 'medium',
	phoneNumber,
	countryCode,
	readOnly,
	required,
	label = 'Phone',
	placeholder = 'Phone Number',
	codeInputName = 'countryCode',
	phoneInputName = 'phoneNumber',
	styles = {},
	codeStyles = {},
	codeSx = {},
	sx = {},
	onPhoneChange,
	onCountryCodeChange,
	onValidityChange,
	hideCodeInputLabel,
	setExternalIsPhoneValid,
	disabled,
	externalIsPhoneValid,
}: PhoneNumberInputProps) => {
	const [updatedCountryCode, setUpdatedCountryCode] = useState<number>(1);
	const [updatedPhoneNumber, setUpdatedPhoneNumber] = useState<string>(phoneNumber || '');
	const [isPhoneValid, setIsPhoneValid] = useState<boolean>(true);

	const validatePhoneNumber = useCallback(
		(pn: string, cc: number) => {
			if (!pn?.length && !required) {
				return;
			}

			const country = getCountry(cc);

			const isValid = isValidPhoneNumber(`${cc}${pn}`, country);

			setIsPhoneValid(isValid);
			// Pass validity to parent component
			if (setExternalIsPhoneValid) {
				setExternalIsPhoneValid(isValid);
			}
		},
		[required, setExternalIsPhoneValid],
	);

	useEffect(() => {
		setUpdatedCountryCode(countryCode || 1);

		if (phoneNumber) {
			const formatted = formatPhoneNumber(phoneNumber, countryCode || 1);
			setUpdatedPhoneNumber(formatted);
			validatePhoneNumber(parseDigits(formatted), countryCode || 1);
		} else {
			setUpdatedPhoneNumber('');
			setIsPhoneValid(!required);
		}
	}, [phoneNumber, countryCode, required, validatePhoneNumber]);

	useEffect(() => {
		// Pass validity to parent component
		if (onValidityChange) {
			onValidityChange(isPhoneValid);
		}
	}, [isPhoneValid, onValidityChange]);

	const handleCountryChange = (country: CountryCode) => {
		const callingCode = getCountryCallingCode(country);

		setUpdatedCountryCode(callingCode);
		onCountryCodeChange(callingCode);
		validatePhoneNumber(updatedPhoneNumber, callingCode);
	};

	const handleFocus = () => {
		setUpdatedPhoneNumber(parseDigits(updatedPhoneNumber));
	};

	const handleBlur = () => {
		setUpdatedPhoneNumber(formatPhoneNumber(updatedPhoneNumber, updatedCountryCode));
		onPhoneChange(parseDigits(updatedPhoneNumber));
		validatePhoneNumber(updatedPhoneNumber, updatedCountryCode);
	};

	const handlePhoneChange = (value: string) => {
		setIsPhoneValid(true);
		setUpdatedPhoneNumber(parseDigits(value));
	};

	const countryCodeError = useMemo(
		() => (!!updatedPhoneNumber.length || required) && !updatedCountryCode && !readOnly,
		[updatedPhoneNumber, updatedCountryCode, readOnly, required],
	);

	const phoneNumberError = useMemo(
		() =>
			(!isPhoneValid && !readOnly) ||
			(externalIsPhoneValid != null && !externalIsPhoneValid) ||
			(required && !updatedPhoneNumber.length),
		[isPhoneValid, externalIsPhoneValid, readOnly, required, updatedPhoneNumber],
	);

	const phoneNumberErrorMessage = useMemo(() => {
		if (required && !updatedPhoneNumber.length) {
			return 'Phone number is required';
		}

		if (!updatedCountryCode) {
			return 'Country code is required';
		}

		return 'Phone number is invalid';
	}, [required, updatedPhoneNumber, updatedCountryCode]);

	return (
		<div style={{ display: 'flex', gap: '10px', width: '100%', ...styles }}>
			<FormControl sx={{ minWidth: '135px', ...codeStyles }} error={countryCodeError && !readOnly}>
				{!hideCodeInputLabel && (
					<InputLabel id='country-code-select-label' shrink>
						Code
					</InputLabel>
				)}
				<Select
					size={size}
					sx={codeSx}
					id='country-code-select'
					label='Code'
					name={codeInputName}
					value={getCountry(updatedCountryCode)}
					onChange={(e) => handleCountryChange(e.target.value as CountryCode)}
					inputProps={{ readOnly }}
					disabled={disabled}
				>
					{countryCodeLib.map((c) => (
						<MenuItem key={c.name} value={c.shortName || ''}>
							{c.code && `+${c.code}`}
						</MenuItem>
					))}
				</Select>
				{countryCodeError && !readOnly && <FormHelperText>Required</FormHelperText>}
			</FormControl>
			<TextField
				size={size}
				id='phone'
				sx={sx}
				label={label}
				placeholder={placeholder}
				name={phoneInputName}
				onChange={(e) => handlePhoneChange(e.target.value)}
				onFocus={handleFocus}
				onBlur={handleBlur}
				InputProps={{ readOnly }}
				value={updatedPhoneNumber || ''}
				type='tel'
				fullWidth
				error={phoneNumberError && !readOnly}
				helperText={phoneNumberError && !readOnly ? phoneNumberErrorMessage : ''}
				disabled={disabled}
			/>
		</div>
	);
};
